import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";
import { Plus, Loader2, PackageOpen } from "lucide-react";
import { useApi, fmtDay } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const Stat = ({ label, value, testId }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-6">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-display text-3xl font-bold text-[#0A192F] mt-2" data-testid={testId}>{value}</p>
  </div>
);

export default function DashboardPage() {
  const api = useApi();
  const { userId } = useAuth();
  const { data = [], isLoading } = useQuery({ queryKey: ["my-shipments", userId], queryFn: () => api("get", "/my/shipments"), staleTime: 0 });
  const delivered = data.filter((s) => s.status === "Delivered").length;
  const pending = data.filter((s) => s.status === "Pending Approval").length;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A192F]">My shipments</h1>
        <Link to="/book" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF6B00] hover:bg-[#E05D00] text-white font-semibold text-sm rounded-sm" data-testid="dashboard-book-btn"><Plus className="h-4 w-4" /> New shipment</Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total" value={data.length} testId="stat-total" />
        <Stat label="Awaiting approval" value={pending} testId="stat-pending" />
        <Stat label="In progress" value={data.length - delivered - pending} testId="stat-active" />
        <Stat label="Delivered" value={delivered} testId="stat-delivered" />
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : data.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-sm p-12 text-center space-y-2" data-testid="dashboard-empty">
          <PackageOpen className="h-10 w-10 mx-auto text-slate-400" />
          <p className="font-display font-semibold">No shipments yet</p>
          <p className="text-sm text-slate-500">Book your first shipment to see it here.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="my-shipments-list">
          {data.map((s) => (
            <Link key={s.id} to={`/track/${s.tracking_number}`} className="bg-white border border-slate-200 rounded-sm p-6 hover:border-[#FF6B00] transition-colors space-y-3" data-testid={`my-shipment-${s.tracking_number}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-bold tracking-wider text-[#FF6B00]">{s.tracking_number}</span>
                <StatusBadge status={s.status} />
              </div>
              <p className="font-display font-semibold text-[#0A192F]">{s.origin} → {s.destination}</p>
              <p className="text-sm text-slate-500">To {s.receiver.name} · {s.package.contents} · booked {fmtDay(s.created_at)}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
